import { utc } from '@date-fns/utc'
import { clsx, type ClassValue } from 'clsx'
import {
	format,
	startOfDay,
	subDays,
	isAfter,
	parseISO,
	isSameDay,
} from 'date-fns'
import { type GetSrcArgs, defaultGetSrc } from 'openimg/react'
import { useEffect, useMemo, useReducer, useRef, useState } from 'react'
import {
	type ShouldRevalidateFunction,
	useFormAction,
	useNavigation,
} from 'react-router'
import { useSpinDelay } from 'spin-delay'
import { twMerge } from 'tailwind-merge'
import { Badge } from '#app/components/ui/badge.tsx'

export function getUserImgSrc(objectKey?: string | null) {
	return objectKey
		? `/resources/images?objectKey=${encodeURIComponent(objectKey)}`
		: '/img/user.png'
}

export function getNoteImgSrc(objectKey: string) {
	return `/resources/images?objectKey=${encodeURIComponent(objectKey)}`
}

export function getImgSrc({
	height,
	optimizerEndpoint,
	src,
	width,
	fit,
	format,
}: GetSrcArgs) {
	// We customize getImgSrc so our src looks nice like this:
	// /resources/images?objectKey=...&h=...&w=...&fit=...&format=...
	// instead of this:
	// /resources/images?src=%2Fresources%2Fimages%3FobjectKey%3D...%26w%3D...%26h%3D...
	if (src.startsWith(optimizerEndpoint)) {
		const [endpoint, query] = src.split('?')
		const searchParams = new URLSearchParams(query)
		searchParams.set('h', height.toString())
		searchParams.set('w', width.toString())
		if (fit) {
			searchParams.set('fit', fit)
		}
		if (format) {
			searchParams.set('format', format)
		}
		return `${endpoint}?${searchParams.toString()}`
	}
	return defaultGetSrc({ height, optimizerEndpoint, src, width, fit, format })
}

export function getErrorMessage(error: unknown) {
	if (typeof error === 'string') return error
	if (
		error &&
		typeof error === 'object' &&
		'message' in error &&
		typeof error.message === 'string'
	) {
		return error.message
	}
	console.error('Unable to get error message for error', error)
	return 'Unknown Error'
}

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

export function getDomainUrl(request: Request) {
	const host =
		request.headers.get('X-Forwarded-Host') ??
		request.headers.get('host') ??
		new URL(request.url).host
	const protocol = request.headers.get('X-Forwarded-Proto') ?? 'http'
	return `${protocol}://${host}`
}

export function getReferrerRoute(request: Request) {
	// spelling errors and whatever makes this annoyingly inconsistent
	// in my own testing, `referer` returned the right value, but 🤷‍♂️
	const referrer =
		request.headers.get('referer') ??
		request.headers.get('referrer') ??
		request.referrer
	const domain = getDomainUrl(request)
	if (referrer?.startsWith(domain)) {
		return referrer.slice(domain.length)
	} else {
		return '/'
	}
}

/**
 * Merge multiple headers objects into one (uses set so headers are overridden)
 */
export function mergeHeaders(
	...headers: Array<ResponseInit['headers'] | null | undefined>
) {
	const merged = new Headers()
	for (const header of headers) {
		if (!header) continue
		for (const [key, value] of new Headers(header).entries()) {
			merged.set(key, value)
		}
	}
	return merged
}

/**
 * Combine multiple header objects into one (uses append so headers are not overridden)
 */
export function combineHeaders(
	...headers: Array<ResponseInit['headers'] | null | undefined>
) {
	const combined = new Headers()
	for (const header of headers) {
		if (!header) continue
		for (const [key, value] of new Headers(header).entries()) {
			combined.append(key, value)
		}
	}
	return combined
}

/**
 * Combine multiple response init objects into one (uses combineHeaders)
 */
export function combineResponseInits(
	...responseInits: Array<ResponseInit | null | undefined>
) {
	let combined: ResponseInit = {}
	for (const responseInit of responseInits) {
		combined = {
			...responseInit,
			headers: combineHeaders(combined.headers, responseInit?.headers),
		}
	}
	return combined
}

/**
 * Returns true if the current navigation is submitting the current route's
 * form. Defaults to the current route's form action and method POST.
 *
 * Defaults state to 'non-idle'
 *
 * NOTE: the default formAction will include query params, but the
 * navigation.formAction will not, so don't use the default formAction if you
 * want to know if a form is submitting without specific query params.
 */
export function useIsPending({
	formAction,
	formMethod = 'POST',
	state = 'non-idle',
}: {
	formAction?: string
	formMethod?: 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE'
	state?: 'submitting' | 'loading' | 'non-idle'
} = {}) {
	const contextualFormAction = useFormAction()
	const navigation = useNavigation()
	const isPendingState =
		state === 'non-idle'
			? navigation.state !== 'idle'
			: navigation.state === state
	return (
		isPendingState &&
		navigation.formAction === (formAction ?? contextualFormAction) &&
		navigation.formMethod === formMethod
	)
}

/**
 * This combines useSpinDelay (from https://npm.im/spin-delay) and useIsPending
 * from our own utilities to give you a nice way to show a loading spinner for
 * a minimum amount of time, even if the request finishes right after the delay.
 *
 * This avoids a flash of loading state regardless of how fast or slow the
 * request is.
 */
export function useDelayedIsPending({
	formAction,
	formMethod,
	delay = 400,
	minDuration = 300,
}: Parameters<typeof useIsPending>[0] &
	Parameters<typeof useSpinDelay>[1] = {}) {
	const isPending = useIsPending({ formAction, formMethod })
	const delayedIsPending = useSpinDelay(isPending, {
		delay,
		minDuration,
	})
	return delayedIsPending
}

function callAll<Args extends Array<unknown>>(
	...fns: Array<((...args: Args) => unknown) | undefined>
) {
	return (...args: Args) => fns.forEach((fn) => fn?.(...args))
}

/**
 * Use this hook with a button and it will make it so the first click sets a
 * `doubleCheck` state to true, and the second click will actually trigger the
 * `onClick` handler. This allows you to have a button that can be like a
 * "are you sure?" experience for the user before doing destructive operations.
 */
export function useDoubleCheck() {
	const [doubleCheck, setDoubleCheck] = useState(false)

	function getButtonProps(
		props?: React.ButtonHTMLAttributes<HTMLButtonElement>,
	) {
		const onBlur: React.ButtonHTMLAttributes<HTMLButtonElement>['onBlur'] =
			() => setDoubleCheck(false)

		const onClick: React.ButtonHTMLAttributes<HTMLButtonElement>['onClick'] =
			doubleCheck
				? undefined
				: (e) => {
						e.preventDefault()
						setDoubleCheck(true)
					}

		const onKeyUp: React.ButtonHTMLAttributes<HTMLButtonElement>['onKeyUp'] = (
			e,
		) => {
			if (e.key === 'Escape') {
				setDoubleCheck(false)
			}
		}

		return {
			...props,
			onBlur: callAll(onBlur, props?.onBlur),
			onClick: callAll(onClick, props?.onClick),
			onKeyUp: callAll(onKeyUp, props?.onKeyUp),
		}
	}

	return { doubleCheck, getButtonProps }
}

/**
 * Simple debounce implementation
 */
function debounce<Callback extends (...args: Parameters<Callback>) => void>(
	fn: Callback,
	delay: number,
) {
	let timer: ReturnType<typeof setTimeout> | null = null
	return (...args: Parameters<Callback>) => {
		if (timer) clearTimeout(timer)
		timer = setTimeout(() => {
			fn(...args)
		}, delay)
	}
}

/**
 * Debounce a callback function
 */
export function useDebounce<
	Callback extends (...args: Parameters<Callback>) => ReturnType<Callback>,
>(callback: Callback, delay: number) {
	const callbackRef = useRef(callback)
	useEffect(() => {
		callbackRef.current = callback
	})
	return useMemo(
		() =>
			debounce(
				(...args: Parameters<Callback>) => callbackRef.current(...args),
				delay,
			),
		[delay],
	)
}

export async function downloadFile(url: string, retries: number = 0) {
	const MAX_RETRIES = 3
	try {
		const response = await fetch(url)
		if (!response.ok) {
			throw new Error(`Failed to fetch image with status ${response.status}`)
		}
		const contentType = response.headers.get('content-type') ?? 'image/jpg'
		const arrayBuffer = await response.arrayBuffer()
		const file = new File([arrayBuffer], 'downloaded-file', {
			type: contentType,
		})
		return file
	} catch (e) {
		if (retries > MAX_RETRIES) throw e
		return downloadFile(url, retries + 1)
	}
}

/**
 * Sleep for a given number of milliseconds
 *
 */
export const sleep = (ms: number) =>
	new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Process a batch of items
 * Used to avoid overwhelming the database when importing/updating large amounts of data
 */

export async function processBatch<T>(
	items: T[],
	batchSize: number,
	processor: ((item: T) => Promise<any>) | ((batch: T[]) => Promise<any>),
) {
	for (let i = 0; i < items.length; i += batchSize) {
		const batch = items.slice(i, i + batchSize)
		if (processor.length === 1) {
			// If processor expects a single argument, process items individually
			await Promise.all(
				batch.map((item) => (processor as (item: T) => Promise<any>)(item)),
			)
		} else {
			// If processor expects multiple arguments, process the whole batch
			await (processor as (batch: T[]) => Promise<any>)(batch)
		}
	}
}

// This hook is used to toggle a boolean state
type UseToggleReturnType = {
	isToggled: boolean
	toggle: () => void
}

export const useToggle = (): UseToggleReturnType => {
	// When calling a `toggle` function, a callback passed to the `useReducer` will be called with the previous state, returning the opposite state.
	const [isToggled, toggle] = useReducer((previousValue) => {
		return !previousValue
		// `false` is the initial state
	}, false)

	return { isToggled, toggle }
}

// This function is used to pluralize a word
export function pluralize(word: String) {
	if (word.endsWith('y')) {
		return word.slice(0, -1) + 'ies'
	} else if (word.endsWith('s')) {
		return word + 'es'
	} else {
		return word + 's'
	}
}

/**
 * Round a number to 2 decimal places for storage
 */
export function roundTwoDecimals(number: number): number {
	return Math.round(number * 100) / 100
}

/**
 * Format a number as a percentage with 2 decimal places
 */
export function formatPercent(number: number, multiplier = false): string {
	const value = multiplier ? number * 100 : number
	return `${value.toFixed(2)}%`
}

// formatTime helper function to just get the time from the DateTime field
export function formatTime(time: string) {
	return format(new Date(time), 'h:mm a', { in: utc })
}

// format minutes to hours
export function formatMinutesToHours(minutes: number | null) {
	if (minutes === null) return '0'
	return minutes / 60
}

// calculate an array of LeafLink orders to get the total
export function calculateTotal(
	orders: { total: { amount: number } }[] | undefined,
): number {
	if (!orders || !orders.length) {
		return 0
	}

	return orders.reduce((acc, order) => acc + order.total.amount, 0)
}

// Strip HTML from a string
export function stripHtml(html: string | null | undefined): string {
	if (!html) return ''

	// Define common HTML entities
	const entities: Record<string, string> = {
		'&nbsp;': ' ',
		'&quot;': '"',
		'&amp;': '&',
		'&lt;': '<',
		'&gt;': '>',
		'&apos;': "'",
		'&ndash;': '–',
		'&mdash;': '—',
		'&lsquo;': "'",
		'&rsquo;': "'",
		'&ldquo;': '"',
		'&rdquo;': '"',
	}

	return (
		html
			// First replace HTML tags
			.replace(/<\/?[^>]+(>|$)/g, ' ')
			// Replace entities with their corresponding characters
			.replace(/&[^\s;]+;/g, (match) => entities[match] || ' ')
			// Remove multiple spaces
			.replace(/\s+/g, ' ')
			// Trim whitespace
			.trim()
			// Limit to reasonable length for meta description
			.slice(0, 155)
			// Add ellipsis if text was truncated
			.replace(/\w+$/, (match) => `${match}...`)
	)
}

// FRONTEND: Replace template variables in meta tags for SEO settings
export function replaceSEOVars(
	text: string,
	variables: Record<string, string>,
) {
	return Object.entries(variables).reduce(
		(result, [variable, value]) =>
			result.replace(new RegExp(variable, 'gi'), value),
		text,
	)
}

// DASHBOARD: Format SEO preview text to show template variables as badges
export function formatSEOPreview(text?: string | null) {
	// Return empty string if text is undefined or null
	if (!text) return ''

	// Replace template variables with badge components
	return text.split(/(%%\w+%%)/).map((part, index) => {
		if (part.match(/%%\w+%%/)) {
			// It's a template variable
			return (
				<Badge variant="secondary" key={index}>
					{part.replace(/%%/g, '')}
				</Badge>
			)
		}
		return part
	})
}

/**
 * Generates a SEO-friendly slug from a string
 * @param text The string to convert into a slug
 * @returns A lowercase string with spaces replaced by hyphens, removing special characters
 * @example
 * generateSlug("Hello World! 123") // returns "hello-world-123"
 * generateSlug("Joe's Tree Service & Removal") // returns "joes-tree-service-removal"
 */
export function generateSlug(text: string): string {
	return (
		text
			// Convert to lowercase
			.toLowerCase()
			// Replace special characters with spaces
			.replace(/[''""]/g, '')
			// Replace & with 'and'
			.replace(/&/g, 'and')
			// Remove all characters that are not letters, numbers, or spaces
			.replace(/[^a-z0-9\s-]/g, '')
			// Replace multiple spaces with single space
			.replace(/\s+/g, ' ')
			// Trim spaces from start and end
			.trim()
			// Replace spaces with hyphens
			.replace(/\s/g, '-')
			// Remove multiple consecutive hyphens
			.replace(/-+/g, '-')
	)
}

// Table Columns: Get the color of a metric based on its value
type MetricRange = {
	value: number
	highThreshold?: number // > 90
	mediumThreshold?: number // > 80
	lowThreshold?: number // > 70
	customHighThreshold?: number
	customMediumThreshold?: number
	customLowThreshold?: number
}

export function getMetricStatusColor({
	value,
	highThreshold = 90,
	mediumThreshold = 80,
	lowThreshold = 75,
	customHighThreshold,
	customMediumThreshold,
	customLowThreshold,
}: MetricRange): string {
	return value > (customHighThreshold ?? highThreshold)
		? 'text-green-500'
		: value > (customMediumThreshold ?? mediumThreshold)
			? 'text-yellow-500'
			: value > (customLowThreshold ?? lowThreshold)
				? 'text-orange-500'
				: 'text-red-500'
}

// Categorize various  by date
interface DateSummary<T> {
	items: T[]
	count: number
	total?: number // Optional since not all items will have amounts
}

interface TimePeriodSummary<T> {
	today: DateSummary<T>
	yesterday: DateSummary<T>
	last7Days: DateSummary<T>
	last14Days: DateSummary<T>
	last30Days: DateSummary<T>
}

function createEmptySummary<T>(): DateSummary<T> {
	return {
		items: [],
		count: 0,
		total: 0,
	}
}

export function categorizeByDate<T>(
	items: T[],
	dateField: keyof T,
	getAmount?: (item: T) => number,
): TimePeriodSummary<T> {
	const now = new Date()
	const today = startOfDay(now)
	const yesterday = subDays(today, 1)
	const last7Days = subDays(today, 7)
	const last14Days = subDays(today, 14)
	const last30Days = subDays(today, 30)

	const result: TimePeriodSummary<T> = {
		today: createEmptySummary(),
		yesterday: createEmptySummary(),
		last7Days: createEmptySummary(),
		last14Days: createEmptySummary(),
		last30Days: createEmptySummary(),
	}

	items.forEach((item) => {
		const itemDate = parseISO(item[dateField] as string)

		const amount = getAmount ? getAmount(item) : 0

		if (isSameDay(itemDate, today)) {
			result.today.items.push(item)
			result.today.count++
			if (getAmount) result.today.total! += amount
		}

		if (isSameDay(itemDate, yesterday)) {
			result.yesterday.items.push(item)
			result.yesterday.count++
			if (getAmount) result.yesterday.total! += amount
		}

		if (isAfter(itemDate, last7Days) || isSameDay(itemDate, last7Days)) {
			result.last7Days.items.push(item)
			result.last7Days.count++
			if (getAmount) result.last7Days.total! += amount
		}

		if (isAfter(itemDate, last14Days) || isSameDay(itemDate, last14Days)) {
			result.last14Days.items.push(item)
			result.last14Days.count++
			if (getAmount) result.last14Days.total! += amount
		}

		if (isAfter(itemDate, last30Days) || isSameDay(itemDate, last30Days)) {
			result.last30Days.items.push(item)
			result.last30Days.count++
			if (getAmount) result.last30Days.total! += amount
		}
	})

	return result
}

// Type Summary: A summary of a type of item
// For example, a type could be a customer order status, activity type,
interface TypeSummary {
	count: number
	total?: number
	percentage: number
}

interface TypeBreakdown<T> {
	[key: string]: TypeSummary
}

interface TimePeriodTypeAnalysis<T> {
	today: TypeBreakdown<T>
	yesterday: TypeBreakdown<T>
	last7Days: TypeBreakdown<T>
	last14Days: TypeBreakdown<T>
	last30Days: TypeBreakdown<T>
}

export function analyzeTypesByDate<T>(
	timePeriods: TimePeriodSummary<T>,
	getType: (item: T) => string,
	getAmount?: (item: T) => number,
): TimePeriodTypeAnalysis<T> {
	function analyzeTypes(items: T[]): TypeBreakdown<T> {
		const breakdown: TypeBreakdown<T> = {}
		const totalItems = items.length

		items.forEach((item) => {
			const type = getType(item)
			if (!breakdown[type]) {
				breakdown[type] = {
					count: 0,
					total: 0,
					percentage: 0,
				}
			}

			breakdown[type].count++
			if (getAmount) {
				breakdown[type].total! += getAmount(item)
			}
		})

		// Calculate percentages
		Object.values(breakdown).forEach((summary) => {
			summary.percentage = (summary.count / totalItems) * 100
		})

		return breakdown
	}

	return {
		today: analyzeTypes(timePeriods.today.items),
		yesterday: analyzeTypes(timePeriods.yesterday.items),
		last7Days: analyzeTypes(timePeriods.last7Days.items),
		last14Days: analyzeTypes(timePeriods.last14Days.items),
		last30Days: analyzeTypes(timePeriods.last30Days.items),
	}
}

/**
 * Format phone numbers for display
 * Handles various input formats and normalizes to XXX-XXX-XXXX
 * @param input Phone number string in any common format
 * @returns Formatted phone number or empty string if invalid
 * @example
 * formatPhoneNumber("+1817-767-9428") // returns "817-767-9428"
 * formatPhoneNumber("(817)-767-9428") // returns "817-767-9428"
 * formatPhoneNumber("8177679428") // returns "817-767-9428"
 * formatPhoneNumber("817-767-9428 x123") // returns "817-767-9428 x123"
 */
export function formatPhoneNumber(input?: string | null): string {
	if (!input) return ''

	const trimmed = input.trim()

	// Capture and remove extension-like suffixes (x123, ext. 123, extension 123)
	const extMatch = trimmed.match(
		/\s*(?:ext\.?|x|extension)\s*[:.]?\s*(\d+)\s*$/i,
	)
	const ext = extMatch?.[1] ?? ''
	const main = extMatch ? trimmed.slice(0, extMatch.index).trim() : trimmed

	// Strip all non-digits
	let digits = main.replace(/\D/g, '')

	// If +1 or leading 1 country code, drop it for display
	if (digits.length === 11 && digits.startsWith('1')) {
		digits = digits.slice(1)
	}

	// Format US 10-digit numbers as XXX-XXX-XXXX
	if (digits.length === 10) {
		const formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
		return ext ? `${formatted} x${ext}` : formatted
	}

	// Format 7-digit local numbers as XXX-XXXX
	if (digits.length === 7) {
		const formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`
		return ext ? `${formatted} x${ext}` : formatted
	}

	// Fallback: return original input if we can't confidently format
	return trimmed
}
